import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router";
import { observer } from "mobx-react";
import { Box, Divider } from "@mui/material";

import { useUploadHistoryStore } from "../../hooks/useUploadHistoryStore";
import { useAppStore } from "../../hooks/useAppStore";
import Column from "../../components/ui/core/Column";
import Row from "../../components/ui/core/Row";
import SummaryCards from "../../components/dashboard/SummaryCards";
import UploadHistoryFilters from "../../components/dashboard/UploadHistoryFilters";
import UploadHistoryTable from "../../components/dashboard/UploadHistoryTable";
import FileDetailViewer from "../../components/dashboard/FileDetailViewer";
import SidebarDelimiter from "../../components/SidebarDelimiter";
import { useIsBillingMember } from "../../contexts/permissions/useIsBillingMember";

const MIN_PREVIEW_PERCENT = 25;
const MAX_PREVIEW_PERCENT = 80;
const DEFAULT_PREVIEW_PERCENT = 35;

const ClaimsDashboard: React.FC = observer(() => {
  const location = useLocation();
  const store = useUploadHistoryStore();
  const { sidebarWidth } = useAppStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const hasBillingPermission = useIsBillingMember();

  const [previewPercent, setPreviewPercent] = useState(DEFAULT_PREVIEW_PERCENT);
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const newWidth = containerRect.right - e.clientX;
      const newPercent = (newWidth / containerWidth) * 100;

      setPreviewPercent(
        Math.max(
          MIN_PREVIEW_PERCENT,
          Math.min(newPercent, MAX_PREVIEW_PERCENT),
        ),
      );
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const availableWidth =
    location.pathname === "/dashboard"
      ? "100%"
      : `calc(100vw - ${sidebarWidth}px)`;

  return (
    <Row
      ref={containerRef}
      sx={{ height: "100%", overflow: "hidden", width: availableWidth }}
    >
      <Column
        sx={{
          p: { xs: 2, sm: 3, md: 3, lg: 4 },
          // gridGap: { xs: "12px", sm: "16px", md: "16px", lg: "20px" },
          backgroundColor: "secondary.main",
          width: store.selectedFile ? `${100 - previewPercent}%` : "100%",
          minWidth: 0,
          overflow: "hidden",
          flexShrink: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          ...(hasBillingPermission ? { pt: "5px !important" } : {}),
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <SummaryCards />
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              background: "white",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "8px",
              boxShadow: "0px 2px 2px 0px #2F3B460D",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <UploadHistoryFilters />

            <Divider />

            <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
              <UploadHistoryTable />
            </Box>
          </Box>
        </Box>
      </Column>

      {store.selectedFile && (
        <>
          <SidebarDelimiter
            onResizeStart={handleMouseDown}
            isResizing={isDragging}
          />

          <Box
            sx={{
              width: `calc(${previewPercent}% - 12px)`,
              height: "100%",
              flexShrink: 0,
              overflow: "hidden",
              backgroundColor: "background.paper",
            }}
          >
            <FileDetailViewer />
          </Box>
        </>
      )}
    </Row>
  );
});

export default ClaimsDashboard;
